import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import './App.css';
import { VIEWPORT_HEIGHT, VIEWPORT_WIDTH } from './domain/Constants';
import {
  computeFitScale,
  getHostViewportSize,
  isDesktopHost,
} from './infrastructure/runtime/fitViewport';
import { AppErrorBoundary } from './presentation/components/AppErrorBoundary';
import { useAudioUnlock } from './presentation/hooks/useAudioUnlock';
import { ScreenCoordinator } from './presentation/navigation/ScreenCoordinator';
import { useAppStore } from './presentation/store/appStore';

type AppLayout = {
  style: CSSProperties;
};

function computeAppLayout(
  size: { width: number; height: number } = getHostViewportSize(),
): AppLayout {
  const scale = computeFitScale(size, isDesktopHost(size));

  // Stretch design canvas to exact host width and height (no letterbox bars).
  return {
    style: {
      width: VIEWPORT_WIDTH,
      height: VIEWPORT_HEIGHT,
      transform: `scale(${scale.x}, ${scale.y})`,
    },
  };
}

function useAppLayout(): AppLayout {
  const [layout, setLayout] = useState(() => computeAppLayout());

  useEffect(() => {
    const update = () => setLayout(computeAppLayout());

    update();
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, []);

  return layout;
}

function AppShell({ children }: { children: ReactNode }) {
  const layout = useAppLayout();
  useAudioUnlock();

  return (
    <div className="App" style={layout.style}>
      {children}
    </div>
  );
}

function App() {
  const hydrationStatus = useAppStore((s) => s.hydrationStatus);
  const hydrationError = useAppStore((s) => s.hydrationError);

  useEffect(() => {
    void useAppStore.getState().bootstrap();
  }, []);

  if (hydrationStatus === 'error') {
    return (
      <AppShell>
        <div className="App-bootstrap App-bootstrap--error">
          <p>Не удалось загрузить прогресс.</p>
          {hydrationError ? <p className="App-bootstrap__detail">{hydrationError}</p> : null}
          <button
            type="button"
            className="App-bootstrap__retry"
            onClick={() => void useAppStore.getState().bootstrap()}
          >
            Повторить
          </button>
        </div>
      </AppShell>
    );
  }

  if (hydrationStatus === 'loading') {
    return (
      <AppShell>
        <div className="App-bootstrap App-bootstrap--loading">Загрузка…</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <AppErrorBoundary>
        <ScreenCoordinator />
      </AppErrorBoundary>
    </AppShell>
  );
}

export default App;
